import {
  Button,
  Flex,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Stack,
  Text,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react'
import {
  faCircleQuestion,
  faClock,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useMemo, useRef } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { noOp } from '@/utils/helpers'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import type { AgeAssuranceSessionConfig } from '@/client/services/ageAssurance/types'
import { getAgeAssuranceVideoAskUrl, getYotiSessionUrl, WEBSITE_URL } from '@/client/services/url'

import { useToaster } from '@/components/Toast/useToaster'
import MenuItem from '@/components/UI/MenuItem'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import ShwLogo from '@/components/UI/ShwLogo'

export type AgeAssuranceSessionModalResult = 'completed' | 'aborted' | 'later'

export default function AgeAssuranceSessionModal({
  sessionConfig,
  isOpen,
  onDone,
}: {
  sessionConfig: AgeAssuranceSessionConfig
  isOpen: boolean
  onDone: (result: AgeAssuranceSessionModalResult) => void
}) {
  const toast = useToaster()
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const {
    isOpen: isVerifyLaterOpen,
    onClose: onVerifyLaterClose,
    onOpen: onVerifyLaterOpen,
  } = useDisclosure()
  const { isOpen: isIssueOpen, onClose: onIssueClose, onOpen: onIssueOpen } = useDisclosure()

  const iframeUrl = useMemo(
    () =>
      sessionConfig.method === 'yoti'
        ? getYotiSessionUrl({ sessionId: sessionConfig.sessionId })
        : getAgeAssuranceVideoAskUrl(),
    [sessionConfig]
  )

  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current
    if (iframe == null) {
      return
    }

    // If the iframe redirects to our site, then the age assurance
    // session completed successfully.
    try {
      const currentUrl = iframe.contentWindow?.location.href
      if (currentUrl != null && currentUrl.includes(WEBSITE_URL)) {
        onDone('completed')
      }
    } catch {
      // Cross-origin access throws - expected when iframe is on external domain
    }
  }, [onDone])

  const [maybeAbortSession] = useAPIWrapper(
    async () => {
      const { aborted } = await RPC.maybeAbortAgeAssuranceSession({})
      if (aborted) {
        toast({
          description: 'You have aborted your age verification session',
          status: 'success',
        })
      }
      onDone('aborted')
    },
    [toast, onDone],
    'Abort Age Assurance Session'
  )

  if (iframeUrl == null) {
    return null
  }

  return (
    <>
      <ModalV3
        title={() => (
          <Flex align="center" gap="12px">
            <ShwLogo height="24px" />
            <Heading size="lg" as="h4">
              Age Verification
            </Heading>
          </Flex>
        )}
        size="full"
        open={isOpen}
        removeBodyPaddingAndMargin
        disableClose
        onClose={noOp}
        body={() => (
          <iframe
            ref={iframeRef}
            key={iframeUrl}
            title="Age Verification"
            src={iframeUrl}
            onLoad={handleLoad}
            style={{
              width: '100%',
              flex: 1,
              borderTop: `1px solid ${chakraTokenToVar('border')}`,
              borderBottom: `1px solid ${chakraTokenToVar('border')}`,
              borderLeft: 'none',
              borderRight: 'none',
            }}
            allow="camera; microphone"
          />
        )}
        rightHeaderContent={() => (
          <Flex gap="8px">
            {isMobile ? (
              <Menu>
                <MenuButton
                  as={IconButton}
                  variant="ghost"
                  aria-label="Options"
                  icon={
                    <FontAwesomeIcon
                      icon={faCircleQuestion}
                      color={chakraTokenToVar('fg')}
                      size="xl"
                    />
                  }
                />
                <MenuList border={`2px solid ${chakraTokenToVar('border.emphasized')}`}>
                  <MenuItem
                    value="verify-later"
                    icon={faClock}
                    label="Verify later"
                    onClick={onVerifyLaterOpen}
                  />
                  <MenuItem
                    value="issue"
                    icon={faTriangleExclamation}
                    color="error.fg"
                    label="There was an issue"
                    onClick={onIssueOpen}
                  />
                </MenuList>
              </Menu>
            ) : (
              <>
                <Button
                  variant="outline"
                  leftIcon={<FontAwesomeIcon icon={faClock} />}
                  onClick={onVerifyLaterOpen}
                >
                  Verify Later
                </Button>
                {sessionConfig.method === 'yoti' ? (
                  <Button
                    variant="danger-outline"
                    leftIcon={<FontAwesomeIcon icon={faTriangleExclamation} />}
                    onClick={onIssueOpen}
                  >
                    There was an issue
                  </Button>
                ) : null}
              </>
            )}
          </Flex>
        )}
      />
      <ModalV3
        title="Verify Later"
        open={isVerifyLaterOpen}
        onClose={onVerifyLaterClose}
        body={() => (
          <Stack gap="16px">
            <Text>
              You won&apos;t be able to join Zoom sessions or complete a tutor application until you
              finish age verification.
            </Text>
            <Text>You can come back and verify anytime from your Settings page.</Text>
            <Text fontWeight="semibold">Do you want to verify later?</Text>
          </Stack>
        )}
        rightFooterContent={() => (
          <Flex
            gap="8px"
            width="100%"
            justifyContent="flex-end"
            flexDirection={{ base: 'column-reverse', sm: 'row' }}
          >
            <Button
              variant="outline"
              width={{ base: '100%', sm: 'auto' }}
              onClick={onVerifyLaterClose}
            >
              Continue Verification
            </Button>
            <Button
              width={{ base: '100%', sm: 'auto' }}
              onClick={() => {
                onDone('later')
                onVerifyLaterClose()
              }}
            >
              Verify Later
            </Button>
          </Flex>
        )}
      />
      <ModalV3
        title="There Was an Issue"
        open={isIssueOpen}
        onClose={onIssueClose}
        body={() => (
          <Stack gap="16px">
            <Text>
              If you&apos;re unable to complete verification because of a technical problem, you can
              submit a manual video for review. Manual reviews take up to 2 business days.
            </Text>
            <Text>If you can complete verification normally, please select Try Again.</Text>
          </Stack>
        )}
        rightFooterContent={() => (
          <Flex
            gap="8px"
            width="100%"
            justifyContent="flex-end"
            flexDirection={{ base: 'column-reverse', sm: 'row' }}
          >
            <Button variant="outline" width={{ base: '100%', sm: 'auto' }} onClick={onIssueClose}>
              Try Again
            </Button>
            <Button
              width={{ base: '100%', sm: 'auto' }}
              onClick={() => {
                onIssueClose()
                maybeAbortSession().catch(noOp)
              }}
            >
              Continue to Manual Verification
            </Button>
          </Flex>
        )}
      />
    </>
  )
}
